import React, { useCallback } from 'react';
import Button from '@atlaskit/button/standard-button';
import { ErrorMessage } from '@atlaskit/form';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalTransition,
	ModalBody,
	ModalTitle,
	ModalHeader,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { Intl, BaseUrl } from '@atlassian/jira-shared-types/src/general.tsx';
import { useDeleteCalendarService } from '../../../../services/delete-calendar/index.tsx';
import messages from './messages';

type Props = {
	isOpen: boolean;
	isOtherProjectCalendar: boolean;
	baseUrl: BaseUrl;
	calendarName: string;
	calendarId: number;
} & Intl & {
		onClose: () => void;
		onDeleteCalendarSuccessful: (calendarRowIndex: number) => void;
	};

const DeleteCalendarModalComponent = ({
	baseUrl,
	isOpen,
	onClose,
	calendarName,
	calendarId,
	onDeleteCalendarSuccessful,
	isOtherProjectCalendar,
	intl: { formatMessage },
}: Props) => {
	const [{ loading, error }, { deleteCalendar }] = useDeleteCalendarService(baseUrl);

	const handleDeleteCalendarClick = useCallback(async () => {
		const response = await deleteCalendar(calendarId, isOtherProjectCalendar);
		if (response) {
			// Calling onClose to toggle DeleteCalendarModal
			onClose();
			onDeleteCalendarSuccessful(calendarId);
		}
	}, [deleteCalendar, calendarId, isOtherProjectCalendar, onClose, onDeleteCalendarSuccessful]);

	return (
		<ModalTransition>
			{isOpen && (
				<ShortcutScope>
					<ModalDialog
						onClose={onClose}
						// @ts-expect-error - Property 'loading' does not exist on props
						loading={loading}
					>
						<ModalHeader>
							<ModalTitle>{formatMessage(messages.deleteCalendarModalHeader)}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<p>{formatMessage(messages.deleteCalendarModalBody)}</p>
							<ul>
								<li>{calendarName}</li>
							</ul>
							{error && <ErrorMessage>{formatMessage(messages.errorMessage)}</ErrorMessage>}
						</ModalBody>
						<ModalFooter>
							<Button
								onClick={handleDeleteCalendarClick}
								appearance="primary"
								testId="servicedesk-sla-settings.ui.calendars.summary-with-actions.delete-modal.delete-calendar-button"
							>
								{formatMessage(messages.deleteButton)}
							</Button>
							<Button onClick={onClose} appearance="subtle">
								{formatMessage(messages.cancelButton)}
							</Button>
						</ModalFooter>
					</ModalDialog>
				</ShortcutScope>
			)}
		</ModalTransition>
	);
};

export const DeleteCalendarModal = injectIntl(DeleteCalendarModalComponent);
